/**
 * trialRecorder.js - writing down the shape of a trial as it happens.
 *
 * PLATFORM SCAFFOLD. Every voice game builds this record, and the echo defence
 * below is universal - the microphone is open while the game talks, in every
 * game. Two games attributing utterances differently would be two charts.
 *
 * PURE. No React, no DOM, no clock: every timestamp arrives from the caller,
 * which is what makes the whole thing testable without a browser, a
 * microphone, or a child. Same split as the engine's own
 * vocalisationDetector / useAudioEnergy pair.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS FOR
 *
 * COMPONENT-JUDGEMENT.md section 4: a game knows the OUTCOME of a trial; the
 * engine needs its SHAPE. Which rung was live when the child spoke, whether a
 * hint had fired, whether Pip had modelled, whether any sound was made at all.
 * 28 of the 208 boxes are judged from this record and from nothing else, and
 * none of it is recoverable after the fact.
 *
 * engine/trial.js defines and validates the record. This builds one.
 *
 * ---------------------------------------------------------------------------
 * ONE CLOCK
 *
 * Every timestamp is performance.now() milliseconds, supplied by the caller.
 * The engine rejects a record whose stamps span more than an hour, because
 * that is Date.now() leaking in somewhere. Do not mix.
 *
 * ---------------------------------------------------------------------------
 * ECHO - THE PART THE ENGINE DOES NOT MODEL
 *
 * The game speaks the target word aloud, through the device's speakers, into a
 * live microphone. Audit finding B42: not one of the four existing games
 * defends against this, and one of them (B43) has five levels that can be
 * passed by the word Pip just said.
 *
 * So the recorder is told when Pip is speaking - pipSpoke(text, from, to) - and
 * refuses to attribute anything inside that window to the child:
 *
 *   - a recognition result whose onset falls inside a Pip window AND whose text
 *     is contained in what Pip just said is dropped as echo. A DIFFERENT word
 *     spoken over Pip is kept, because that is the child interrupting, which is
 *     real and often the most interesting thing they do.
 *   - a vocalisation from the audio envelope that overlaps a Pip window is
 *     dropped outright. There is no text to compare, so there is no way to tell
 *     the child's "buh" from Pip's own voice, and crediting the speaker with
 *     the child's box is worse than losing the box.
 *
 * Both are conservative: they cost the child credit they may have earned rather
 * than awarding credit they did not. The Pip windows are not part of the trial
 * record - engine/trial.js rejects unknown fields - so they are returned in
 * diagnostics() for the game's own log. See docs/audit/GAME-01.md section 7.5.
 */

import { MIN_VOCALISATION_MS } from '../../../engine/trial.js';

/** Extra milliseconds after Pip stops in which a result is still suspect. */
export const ECHO_GUARD_MS = 300;

const normalise = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {number} input.attemptNumber      1 = the first trial for this step
 * @param {string} input.nominalRungId      SUPPORT_LADDER id the trial opens at
 * @param {number} input.startedAt          performance.now() at trial open
 * @param {number} input.responseWindowMs
 * @param {string} input.targetWord         what the child is expected to say
 * @param {string} [input.targetObject]     the object involved
 * @param {string} [input.targetPosition]   where it is on screen
 * @param {string[]} [input.legalMoves]     every object the child could name now
 * @param {object} [input.gridState]        the field, for later re-scoring
 * @param {boolean} [input.optionsVisible]  word frames on screen (phase 3)
 * @param {boolean} [input.modelScheduled]  would this trial's prompt sequence
 *        model the target if the child said nothing? Evidence for the
 *        framework's model_opportunity condition; the ENGINE decides what it
 *        means for Phase 3 D.
 * @param {number|null} [input.priorModelAgoMs] how long before this trial
 *        opened this target was last modelled. Recorded beside Phase 3 D, and
 *        deliberately not folded into it - D's window is the trial.
 * @returns {object} recorder
 */
export function createTrialRecorder({
  phase,
  level,
  attemptNumber = 1,
  nominalRungId,
  startedAt,
  responseWindowMs,
  targetWord,
  targetObject = null,
  targetPosition = null,
  legalMoves = [],
  gridState = null,
  optionsVisible = false,
  modelScheduled = false,
  priorModelAgoMs = null,
}) {
  if (!Number.isFinite(startedAt)) throw new Error('createTrialRecorder needs startedAt (performance.now()).');
  if (!nominalRungId) throw new Error('createTrialRecorder needs the rung the trial opens at.');

  const supportSequence = [{ rungId: nominalRungId, at: startedAt }];
  const pipWindows = [];
  const utterances = [];
  const droppedEchoes = [];

  let promptEndedAt = null;
  let hintFiredAt = null;
  let modelEndedAt = null;
  let onsetAt = null;
  let lastFinalAt = null;
  let options = Boolean(optionsVisible);

  const insidePipWindow = (at) =>
    pipWindows.find((w) => at >= w.from && at <= w.to) || null;

  return {
    /** The prompt has finished rendering and Pip has stopped talking. */
    promptEnded(at) {
      promptEndedAt = at;
    },

    /** The support rung changed mid-trial. Only ever toward MORE support. */
    supportChanged(rungId, at) {
      const last = supportSequence[supportSequence.length - 1];
      if (last && last.rungId === rungId) return;
      supportSequence.push({ rungId, at });
    },

    /**
     * Pip gave the semantic clue. First one only: `hintFired` is a boolean in
     * the record, and the timestamp that matters is when help first arrived.
     */
    hintFired(at) {
      if (hintFiredAt === null) hintFiredAt = at;
    },

    /**
     * Pip said the target aloud. EARLIEST model wins, not latest: the question
     * every judgement asks is whether the child had heard a model before they
     * spoke, and a later model cannot un-hear the first one.
     */
    modelEnded(at) {
      if (modelEndedAt === null || at < modelEndedAt) modelEndedAt = at;
    },

    /** Word frames / object choices became visible. */
    optionsShown() {
      options = true;
    },

    /**
     * Pip has STARTED speaking. Called before the utterance, because a result
     * that arrives while Pip is mid-sentence has to be testable against what
     * Pip is saying - and if the window only existed afterwards, the echo of
     * Pip's own voice would be accepted as the child's answer.
     *
     * The window is left open (`to: Infinity`) until pipSpoke closes it, so
     * anything arriving during the utterance is inside it.
     */
    pipSpeaking(text, from) {
      pipWindows.push({ text: normalise(text), from, to: Infinity, spokenUntil: from, open: true });
    },

    /**
     * Pip has stopped. Closes the window opened by pipSpeaking, or opens and
     * closes one in a single call. The echo guard is added here so callers
     * cannot forget it.
     */
    pipSpoke(text, from, to) {
      const open = pipWindows.find((w) => w.open && w.from === from);
      if (open) {
        open.to = to + ECHO_GUARD_MS;
        open.spokenUntil = to;
        open.open = false;
        return;
      }
      pipWindows.push({ text: normalise(text), from, to: to + ECHO_GUARD_MS, spokenUntil: to, open: false });
    },

    /** Is this result Pip's own voice coming back? */
    isEcho(at, text) {
      const window = insidePipWindow(at);
      if (!window) return false;
      const heard = normalise(text);
      if (!heard) return true;
      return window.text.includes(heard) || heard.includes(window.text);
    },

    /**
     * Speech onset - the recogniser's onspeechstart, not an interim result.
     * Earliest non-echo onset wins.
     */
    speechStarted(at) {
      if (insidePipWindow(at)) return false;
      if (onsetAt === null || at < onsetAt) onsetAt = at;
      return true;
    },

    /**
     * A final recognition result the game has decided to act on.
     * @returns {number} its index within the trial
     */
    utterance({ transcript, confidence = null, alternatives = [], at }) {
      const index = utterances.length;
      utterances.push({
        transcript,
        confidence,
        alternatives,
        at,
        gapBeforeMs: lastFinalAt === null ? null : at - lastFinalAt,
      });
      lastFinalAt = at;
      return index;
    },

    /** Record that a result was discarded as Pip's echo. Diagnostics only. */
    echoDropped({ transcript, at }) {
      droppedEchoes.push({ transcript, at });
    },

    /**
     * Build the record engine/trial.js validates.
     *
     * @param {object} input
     * @param {number} input.endedAt
     * @param {object} input.audio  the useAudioEnergy block, or UNMONITORED_AUDIO
     * @param {boolean} [input.allowInferredOnset] when the device offers
     *        neither onspeechstart nor audio monitoring, fall back to the
     *        arrival time of the final result as the onset. It is late, which
     *        costs the child boxes rather than awarding them - see
     *        docs/audit/GAME-01.md section 7.6.
     * @returns {object} a RAW record; pass it through validateTrialRecord()
     */
    build({ endedAt, audio, allowInferredOnset = false }) {
      // A trial can close while Pip is still talking - the response window
      // expires on its own timer. An open window would otherwise reach to
      // infinity and discard every vocalisation after it.
      for (const w of pipWindows) {
        if (w.open) { w.to = endedAt; w.spokenUntil = endedAt; w.open = false; }
      }
      const promptAt = promptEndedAt === null ? startedAt : promptEndedAt;
      const cleanAudio = filterAudio(audio, pipWindows);
      const last = utterances[utterances.length - 1] || null;

      let speechStartedAt = onsetAt;
      let onsetSource = onsetAt === null ? null : 'speech_onset';

      if (speechStartedAt === null) {
        const firstVocalisation = (cleanAudio.vocalisations || [])
          .filter((v) => v.durationMs >= MIN_VOCALISATION_MS && v.startedAt >= promptAt)
          .sort((a, b) => a.startedAt - b.startedAt)[0];
        if (firstVocalisation) {
          speechStartedAt = firstVocalisation.startedAt;
          onsetSource = 'audio_energy';
        }
      }

      if (speechStartedAt === null && allowInferredOnset && last) {
        speechStartedAt = last.at;
        onsetSource = 'inferred_from_final';
      }

      return {
        // ---- the trial window
        trialStartedAt: startedAt,
        promptEndedAt: promptAt,
        trialEndedAt: endedAt,
        responseWindowMs,

        // ---- when the child spoke
        speechStartedAt,
        // Which signal produced it. A chart built on onsets inferred from the
        // arrival of a final result is a different measurement from one built
        // on the recogniser's own onspeechstart, and only the game knows which
        // it was.
        onsetSource,
        finalAt: last ? last.at : null,

        // ---- what support was on screen
        supportSequence,
        modelPlayed: modelEndedAt !== null,
        modelEndedAt,
        modelScheduled,
        priorModelAgoMs,
        hintFired: hintFiredAt !== null,
        hintFiredAt,
        optionsVisible: options,

        // ---- the utterance
        transcript: last ? last.transcript : null,
        confidence: last ? last.confidence : null,
        alternatives: last ? last.alternatives : [],
        utteranceIndex: last ? utterances.length - 1 : 0,
        gapBeforeMs: last ? last.gapBeforeMs : null,

        // ---- the audio path
        audio: cleanAudio,

        // ---- the situation
        targetWord,
        targetObject,
        targetPosition,
        legalMoves,
        gridState,
        attemptNumber,
      };
    },

    /**
     * Everything the record has no field for. The game logs this locally; it
     * is not sent, because engine/trial.js rejects unknown keys.
     */
    diagnostics() {
      return {
        phase,
        level,
        pipWindows: pipWindows.map((w) => ({ text: w.text, from: w.from, to: w.spokenUntil })),
        droppedEchoes: droppedEchoes.slice(),
        utterances: utterances.length,
        onsetAt,
      };
    },

    /** For the caller's own decisions mid-trial. */
    state() {
      return {
        promptEndedAt,
        hintFiredAt,
        modelEndedAt,
        onsetAt,
        rungId: supportSequence[supportSequence.length - 1].rungId,
        utterances: utterances.length,
      };
    },
  };
}

/**
 * Drop vocalisations that overlap a window in which Pip was speaking.
 *
 * `monitored: false` is preserved untouched. The engine reads it as "not
 * measured" and falls back to the transcript; turning it into an empty
 * monitored block would assert a silence nobody observed and score Phase 1 B
 * as a failure the child did not earn.
 */
export function filterAudio(audio, pipWindows = []) {
  if (!audio) return { noiseFloor: null, monitored: false, vocalisations: [], envelope: [] };
  if (!audio.monitored) return audio;
  if (!pipWindows.length) return audio;

  const overlaps = (v) =>
    pipWindows.some((w) => v.startedAt <= w.to && v.endedAt >= w.from);

  return {
    ...audio,
    vocalisations: (audio.vocalisations || []).filter((v) => !overlaps(v)),
  };
}

/**
 * Drop the RMS envelope before an audio block is posted.
 *
 * The envelope is a debugging aid: nothing in the engine judges from it -
 * trial.qualifyingVocalisations() reads only the vocalisation events - and at
 * animation-frame rate a fifteen-second trial carries roughly nine hundred
 * samples. Posted and stored as JSONB, that is tens of kilobytes per utterance
 * for a signal no judgement uses. Kept locally for the session, dropped on the
 * way out. See docs/audit/GAME-01.md section 7.7.
 */
export function slimAudio(audio) {
  if (!audio) return audio;
  if (!audio.envelope || !audio.envelope.length) return audio;
  return { ...audio, envelope: [] };
}

export default { createTrialRecorder, filterAudio, slimAudio, ECHO_GUARD_MS };
